import { BlobUpload } from "@rails/activestorage/src/blob_upload";
import { patch, post, get } from "@rails/request.js";

import { directUploadFileParamsFor } from "../Utils/directUploadUtils";
import { flatMap } from "lodash";
import { sleep } from "../Utils/sleep";

const DIRECT_UPLOAD_PATH = "/uploads";
const PROCESSING_STATUS_CHECK_DELAY = 2000;
const PREPARE_RETRY_DELAYS = [2000, 5000];

export const UPLOAD_STATE_PREPARING = "preparing";
export const UPLOAD_STATE_TRANSFERRING = "transferring";
export const UPLOAD_STATE_SCANNING = "scanning";

export const directUploadFile = async ({
  file,
  onProgress,
  onStepChange,
  onUploadStart,
  onComplete,
  onError,
  directUploadUrl = DIRECT_UPLOAD_PATH,
  attachmentToken
}) => {
  // Prepare the upload by creating the blob in the backend, and fetching the direct
  // upload URL and headers from the server.
  const prepareUpload = async () => {
    const fileParams = await directUploadFileParamsFor(file);
    const body = { ...fileParams, attachment_token: attachmentToken };

    // We fetch the direct upload URL and headers from the server, which are needed
    // to upload the file directly to the storage service.
    const response = await post(directUploadUrl, {
      contentType: "application/json",
      responseKind: "json",
      body
    });

    return await response.json;
  };

  // Compute the progress of the upload and call the onProgress callback if provided.
  const handleTransferProgress = event => {
    if (!onProgress) return;

    const { lengthComputable, loaded, total } = event;

    if (!lengthComputable) return;

    onProgress((loaded / total) * 100);
  };

  // This function is called when the file is ready to be uploaded, and it fetches the
  // direct upload URL and headers from the server, and starts the upload process.
  const upload = ({ directUploadData, ...fileData }) => {
    return new Promise((resolve, reject) => {
      // We create a new BlobUpload instance, which is from the ActiveStorage's package
      // source code, to handle the upload of the file to the storage service.
      const blobUpload = new BlobUpload({ file, directUploadData });

      // We add an event listener to the XHR upload object to track the progress of the
      // upload, and call the onProgress callback with the percentage of the upload.
      blobUpload.xhr.upload.addEventListener("progress", handleTransferProgress);

      // Start the upload through the BlobUpload instance, and handle any error or its
      // completion with the appropriate callbacks.
      blobUpload.create(error => {
        if (error) return reject(error);

        resolve();
      });

      if (onUploadStart) onUploadStart(fileData);
    });
  };

  // Notify the backend that the direct upload is complete on the storage service
  const finalizeUpload = async successCallbackUrl => {
    const response = await patch(successCallbackUrl, {
      contentType: "application/json",
      responseKind: "json"
    });

    return await response.json;
  };

  const handleProcessing = async processingData => {
    const { status, statusUrl, ...data } = processingData;

    if (status === "success") return data;

    if (status === "error") {
      const error = new Error(data.message);
      error.errors = [data.message];

      throw error;
    }

    await sleep(PROCESSING_STATUS_CHECK_DELAY);

    // If the processing is still in progress, we need to poll the server to check
    // the status of the processing.
    const response = await get(statusUrl, { responseKind: "json" });
    const nextProcessingData = await response.json;

    return await handleProcessing(nextProcessingData);
  };

  // Returns uploadData on success or throws on total failure. Never calls onError
  // directly — error dispatch is the caller's responsibility, consistent with the
  // rest of this function.
  const prepareUploadWithRetry = async () => {
    let uploadData = await prepareUpload();

    for (const delay of PREPARE_RETRY_DELAYS) {
      if (uploadData.directUploadData || uploadData.errors) break;

      await sleep(delay);
      uploadData = await prepareUpload();
    }

    if (!uploadData.directUploadData && !uploadData.errors) {
      throw new Error("Failed to prepare the upload after multiple attempts");
    }

    return uploadData;
  };

  if (onStepChange) onStepChange(UPLOAD_STATE_PREPARING);

  // We retry the prepare upload step a few times if it doesn't return the expected
  // directUploadData as there can be issues with OVH backend storage service sometimes
  // and this step is more likely to fail than the upload itself.
  let uploadData;
  try {
    uploadData = await prepareUploadWithRetry();
  } catch (error) {
    if (onError) {
      return onError(error.message);
    }
    throw error;
  }

  if (uploadData.errors) {
    // Flatten the errors object to an array of error messages, as we don't need the keys
    // and assign it to the error object to be used by parent components to display the
    // error messages on the relevant input field.
    const messages = flatMap(Object.values(uploadData.errors));

    if (onError) {
      return onError(messages.join("\n"));
    } else {
      const error = new Error("Failed to prepare the upload");
      error.errors = messages;

      throw error;
    }
  }

  if (onStepChange) onStepChange(UPLOAD_STATE_TRANSFERRING);

  try {
    await upload(uploadData);
  } catch (error) {
    if (onError) {
      return onError(error.message);
    } else {
      // If the upload fails, use the same structure than the validation errors so parent
      // components can use the same error handling logic.
      error.errors = [error.message];

      throw error;
    }
  }

  if (onProgress) onProgress(100);
  if (onStepChange) onStepChange(UPLOAD_STATE_SCANNING);

  const processingData = await finalizeUpload(uploadData.successCallbackUrl);

  const data = await handleProcessing(processingData);

  // Call the onComplete callback with the data returned from the server, which allows
  // the caller to use the file URL or any other data returned from the server.
  onComplete(data);
};
